import { useState, useEffect } from "react";
import { ChevronRight, Download, Lock } from "lucide-react";
import { useAdminData } from "../../hooks/useAdminData";
import { settings as settingsApi } from "../../api/endpoints";
import { ChangePasswordDialog } from "../../components/ChangePasswordDialog";
import { downloadCsv } from "../../exportCsv";
import { AddToast } from "../../components/feedback/Toast";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { PageHeader } from "../../components/ui/PageHeader";
import { SectionLabel } from "../../components/ui/SectionLabel";
import { CORAL, F, T } from "../../theme/tokens";

// ── Settings ──────────────────────────────────────────────────────────────────

export function SettingsView({ addToast, onOpenVerificationForms }: { addToast: AddToast; onOpenVerificationForms: () => void }) {
  const { AUDIT_LOG } = useAdminData();

  const [passwordOpen, setPasswordOpen] = useState(false);

  function exportAuditLog() {
    const count = downloadCsv("donatella-audit-log", AUDIT_LOG, [
      { header: "Timestamp", value: (e) => e.timestamp },
      { header: "Admin", value: (e) => e.admin },
      { header: "Action", value: (e) => e.action },
      { header: "Entity type", value: (e) => e.entityType },
      { header: "Entity", value: (e) => e.entityName },
      { header: "Entity id", value: (e) => e.entityId },
      { header: "Previous", value: (e) => e.previousState },
      { header: "New", value: (e) => e.newState },
      { header: "Result", value: (e) => e.result },
      { header: "IP", value: (e) => e.ip },
    ]);
    addToast(`Exported ${count} audit entries`, "success");
  }

  const [platformFee, setPlatformFee] = useState("10");
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [autoVerif, setAutoVerif] = useState(false);

  useEffect(() => {
    settingsApi.get().then((s) => {
      setPlatformFee(String(s.platform_fee));
      setMaintenanceMode(s.maintenance_mode);
      setAutoVerif(s.auto_verification);
    }).catch(() => { });
  }, []);

  function saveFee() {
    settingsApi.update({ platform_fee: Number(platformFee) })
      .then(() => addToast("Settings saved", "success"))
      .catch(() => addToast("Failed to save settings", "error"));
  }
  function toggleMaintenance() {
    const next = !maintenanceMode;
    setMaintenanceMode(next);
    settingsApi.update({ maintenance_mode: next })
      .then(() => addToast(next ? "Maintenance mode ON" : "Maintenance mode off", next ? "error" : "success"))
      .catch(() => addToast("Failed to update setting", "error"));
  }
  function toggleAutoVerif() {
    const next = !autoVerif;
    setAutoVerif(next);
    settingsApi.update({ auto_verification: next })
      .then(() => addToast("Setting updated", "info"))
      .catch(() => addToast("Failed to update setting", "error"));
  }

  function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
    return (
      <button onClick={onChange} style={{ width: 36, height: 20, borderRadius: "10px", background: checked ? CORAL : T.border, border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
        <span style={{ position: "absolute", top: 2, left: checked ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
      </button>
    );
  }

  return (
    <div>
      <PageHeader title="Settings" sub="Platform configuration" />
      <div style={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: "640px" }}>
        <Card style={{ padding: "20px 22px" }}>
          <SectionLabel>Financial</SectionLabel>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
            <div>
              <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F }}>Platform fee (%)</p>
              <p style={{ fontSize: "11px", color: T.sub, fontFamily: F }}>Applied to all completed project payments</p>
            </div>
            <input value={platformFee} onChange={(e) => setPlatformFee(e.target.value)} type="number" min={0} max={30} style={{ width: "64px", border: `1px solid ${T.border}`, borderRadius: "7px", padding: "6px 10px", fontSize: "13px", fontFamily: F, textAlign: "center", outline: "none" }} />
          </div>
          <Btn variant="primary" onClick={saveFee}>Save changes</Btn>
        </Card>

        <Card style={{ padding: "20px 22px" }}>
          <SectionLabel>Operations</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F }}>Maintenance mode</p>
                <p style={{ fontSize: "11px", color: T.sub, fontFamily: F }}>Shows a maintenance page to all users</p>
              </div>
              <Toggle checked={maintenanceMode} onChange={toggleMaintenance} />
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F }}>Auto-verify new talent</p>
                <p style={{ fontSize: "11px", color: T.sub, fontFamily: F }}>Skip manual review for new registrations (not recommended)</p>
              </div>
              <Toggle checked={autoVerif} onChange={toggleAutoVerif} />
            </div>
          </div>
        </Card>

        <Card style={{ padding: "20px 22px" }}>
          <SectionLabel>Verification</SectionLabel>
          <button onClick={onOpenVerificationForms} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}>
            <div>
              <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F }}>Verification forms</p>
              <p style={{ fontSize: "11px", color: T.sub, fontFamily: F }}>Choose what talent and organisations must provide to get verified</p>
            </div>
            <ChevronRight size={16} style={{ color: T.muted, flexShrink: 0 }} />
          </button>
        </Card>

        <Card style={{ padding: "20px 22px" }}>
          <SectionLabel>Security</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {/* Session termination and 2FA enforcement have no backend behind
                them — they were success toasts that changed nothing, so they are
                gone rather than misleading. The audit export is real. */}
            <Btn variant="ghost" onClick={() => setPasswordOpen(true)} style={{ justifyContent: "flex-start" }}><Lock size={13} /> Change my password</Btn>
            <Btn variant="ghost" onClick={exportAuditLog} style={{ justifyContent: "flex-start" }}><Download size={13} /> Export full audit log</Btn>
            <p style={{ fontSize: "11px", color: T.muted, fontFamily: F, lineHeight: 1.6, margin: 0 }}>
              <Lock size={11} style={{ verticalAlign: "-1px" }} /> Admin sessions are JWTs that expire after 7 days. To revoke one now, set that admin to Inactive under Admin users.
            </p>
          </div>
        </Card>
      </div>
      {passwordOpen && <ChangePasswordDialog onClose={() => setPasswordOpen(false)} />}
    </div>
  );
}
